<template>
    <page-layout :readonly="isProposalAccepted" :hide-footer="isProposalAccepted">
        <template slot="header">
            <proposal-step-header
                :current-step="stepNumber"
                :title="pageTitle"
                :total-steps="totalSteps"
                :is-loading="isLoading"
                :use-exit-modal="!isProposalAccepted && exitConfirmationRequired"
            />
        </template>
        <template slot="default">
            <g-loader type="fixed" v-if="isLoading"/>
            <template v-else>
                <div class="margin-bottom-large">
                    <g-typography tag="p" :value="stepOverview" class="break-new-line"/>
                </div>
                <error-message-card class="margin-bottom-normal" v-if="errorMessage" :message="errorMessage"/>
                <proposal-step-error class="margin-bottom-normal" v-if="proposalStep && proposalStep.error" :step="proposalStep"/>
                <g-form>
                    <template v-if="hasPhoneField">
                        <div class="margin-bottom-normal">
                            <g-typography tag="h5" font-weight="semibold" value="Required"/>
                        </div>
                        <g-form-item :required="true" label="Phone Number" :errors="errors.phone_number">
                            <phone-number-input v-model="personalInfo.phoneNumber" :countries="countries" @phone-input="applyPhoneNumber"/>
                        </g-form-item>
                    </template>
                    <template v-if="hasOptionalFields">
                        <div class="margin-bottom-normal">
                            <g-typography tag="h5" font-weight="semibold" value="Optional"/>
                        </div>
                        <template v-if="hasGenderField">
                            <g-form-item label="Gender" :errors="errors.gender">
                                <g-select placeholder="Select" v-model="personalInfo.gender" style="width: 100%;">
                                    <g-select-option value="Male">Male</g-select-option>
                                    <g-select-option value="Female">Female</g-select-option>
                                    <g-select-option value="Other">Other</g-select-option>
                                </g-select>
                            </g-form-item>
                        </template>
                        <template v-for="field in fields">
                            <g-form-item>
                                <custom-field-input :field="field" @input-value="applyValue"/>
                            </g-form-item>
                        </template>
                    </template>
                </g-form>
            </template>
        </template>
        <template slot="footer">
            <proposal-step-footer
                :is-next-disabled="isDisabled"
                :loading="isCompletingProposal"
                :step-completed="isCompleted"
                :current-step="stepNumber"
                :total-steps="totalSteps"
                :use-exit-modal="!isProposalAccepted && exitConfirmationRequired"
                @go-next-step="goNextStep"
                @go-previous-step="$emit('go-previous-step', proposalStep)"
            />
        </template>
    </page-layout>
</template>

<script>
    import PageLayout from "../../layouts/PageLayout";
    import ProposalStepHeader from "./partials/ProposalStepHeader";
    import ProposalStepFooter from "./partials/ProposalStepFooter";
    import {
        getCustomizableTitle,
        getProposalStepOverview
    } from "../../../helpers/default-texts";
    import PhoneNumberInput from "./partials/PhoneNumberInput";
    import * as _ from "lodash";
    import CustomFieldInput from "./partials/CustomFieldInput";
    import {
        FIELD_SINGLE_CHECKBOX,
        FIELD_MULTIPLE_CHECKBOXES,
    } from "../../../constants/custom-field-types";
    import ErrorMessageCard from "./partials/ErrorMessageCard";
    import PersonalInfoMixin from "./mixins/PersonalInfoMixin";
    import ProposalStepError from "./partials/PoposalStepError";
    import ProposalMixin from "./mixins/ProposalMixin";
    import ExitConfirmationMixin from "./mixins/ExitConfirmationMixin";

    export default {
        name: "ProposalPersonalInfoView",
        components: {
            ProposalStepError,
            ErrorMessageCard,
            CustomFieldInput,
            PhoneNumberInput,
            PageLayout,
            ProposalStepHeader,
            ProposalStepFooter,
        },
        mixins: [
            PersonalInfoMixin,
            ProposalMixin,
            ExitConfirmationMixin
        ],
        props: {
            campaign: {
                type: Object,
            },
            proposal: {
                type: Object,
            },
            contact: {
                type: Object,
            },
            fields: {
                type: Array,
            },
            countries: {
                type: Array,
            },
            proposalStep: {
                type: Object,
            },
            isLoading: {
                type: Boolean,
                default: false,
            },
            isCompletingProposal: {
                type: Boolean,
                default: false,
            },
            errors: {
                type: undefined,
            },
            errorMessage: {
                type: String,
            }
        },
        data() {
            return {
                personalInfo: {
                    phoneNumber: {
                        number: undefined,
                        countryCode: undefined,
                    },
                    gender: undefined,
                    customFields: {},
                },
                initialPersonalInfo: null
            };
        },
        computed: {
            pageTitle() {
                return getCustomizableTitle(this.campaign, 'other_info_step_title');
            },
            stepOverview() {
                return getProposalStepOverview(
                    this.campaign,
                    'other_info_step_overview',
                    'Knowing more about you helps us find campaigns that are better suited to you.'
                );
            },
            isDisabled() {
                return this.hasPhoneField && (!this.isPhoneNumberEntered || !this.isPhoneCountryCodeSelected);
            },
            isPhoneNumberEntered() {
                return !!this.personalInfo.phoneNumber.number;
            },
            isPhoneCountryCodeSelected() {
                return !!this.personalInfo.phoneNumber.countryCode;
            },
            stepNumber() {
                return _.get(this, 'proposalStep.meta.stepNumber', 0);
            },
            totalSteps() {
                return _.get(this, 'proposalStep.meta.totalSteps', 0);
            },
            formPayload() {
                const payload = {};
                if (this.hasPhoneField) {
                    payload['phone_number'] = this.personalInfo.phoneNumber.number ? this.personalInfo.phoneNumber.number : null;
                    payload['phone_country_code'] = this.personalInfo.phoneNumber.countryCode ? this.personalInfo.phoneNumber.countryCode : null;
                }
                if (this.hasGenderField) {
                    payload['gender'] = this.personalInfo.gender ? this.personalInfo.gender : null;
                }

                return Object.assign(payload, _.cloneDeep(this.personalInfo.customFields));
            },
            isCompleted() {
                return _.get(this, 'proposalStep.is_complete', false);
            },
            initialStepState() {
                return this.initialPersonalInfo;
            },
            currentStepState() {

                return this.personalInfo;
            }
        },
        methods: {
            applyPhoneNumber(data) {
                this.personalInfo.phoneNumber.number      = data.phone;
                this.personalInfo.phoneNumber.countryCode = data.country;
            },
            applyValue(data) {
                this.personalInfo.customFields[`field_${data.field_id}`] = data.value;
            },
            seedContactFields(contact) {
                this.personalInfo.gender                  = contact.gender !== null ? contact.gender : undefined;
                this.personalInfo.phoneNumber.number      = contact.phone_number !== null ? contact.phone_number : undefined;
                this.personalInfo.phoneNumber.countryCode = contact.phone_country_code !== null ? contact.phone_country_code : undefined;

                this.updateInitialState();
            },
            seedCustomFields(fields) {
                for (const field of fields) {
                    if (field.value !== null) {
                        let fieldValue = field.value;
                        if (field.type === FIELD_SINGLE_CHECKBOX) {
                            fieldValue = !!field.value;
                        } else if (field.type === FIELD_MULTIPLE_CHECKBOXES) {
                            fieldValue = field.value.split(', ').map(val => val.toString().trim());
                        }

                        this.applyValue({field_id: field.id, value: fieldValue ? fieldValue : null});
                    }
                }

                this.updateInitialState();
            },
            goNextStep() {
                this.$emit('go-next-step', this.proposalStep, this.formPayload);
            },
            updateInitialState() {
                this.initialPersonalInfo = _.cloneDeep(this.personalInfo);
            }
        },
        watch: {
            contact: {
                handler(contact) {
                    this.seedContactFields(contact);
                },
                deep: true,
            },
            fields: {
                handler(fields) {
                    this.seedCustomFields(fields);
                },
                deep: true,
            },
        },
        created() {
            if (this.contact) {
                this.seedContactFields(this.contact);
            }
            if (this.fields) {
                this.seedCustomFields(this.fields);
            }
        }
    }
</script>

<style lang="scss" scoped>
    .margin-bottom-small {
        margin-bottom: $g-spacing-3;
    }

    .margin-bottom-normal {
        margin-bottom: $g-spacing-5;
    }

    .margin-bottom-large {
        margin-bottom: $g-spacing-6;
    }

    .break-new-line {
        white-space: pre-line;
    }
</style>